import React from 'react';
import { Facebook, Instagram, Mail, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SOCIAL_LINKS } from '../data/siteContent';

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, tiktok: TikTokIcon };

const Footer = ({ onNavigate }) => {
  const navigate = useNavigate();

  return (
    <footer className="bg-[#001B2A] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <img src="/bowtie-logo.png" alt="The Bow Tie Guy logo" className="h-9 w-9 object-contain" data-testid="footer-logo" />
              <h3 className="text-2xl font-serif font-bold">THE BOW TIE GUY</h3>
            </div>
            <p className="text-[#E6D7B3] text-xs tracking-[0.25em] mb-4">MENS FASHION</p>
            <p className="text-gray-300 text-sm mb-6">
              Premium bow ties and menswear for the distinguished gentleman. Expert style guidance from Johnnie Mayo since 1977.
            </p>
            <div className="flex space-x-4">
              {SOCIAL_LINKS.map(({ id, label, url }) => {
                const Icon = SOCIAL_ICONS[id];
                return (
                  <a
                    key={id}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={`footer-social-${id}`}
                    className="p-2 bg-white/10 rounded-full hover:bg-[#7B1E2B] transition-colors"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
              <a
                href="mailto:Mensbowtie@yahoo.com"
                aria-label="Email"
                data-testid="footer-social-email"
                className="p-2 bg-white/10 rounded-full hover:bg-[#7B1E2B] transition-colors"
              >
                <Mail className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Collections */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Collections</h4>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>
                <button onClick={() => onNavigate('bow-ties')} className="hover:text-[#E6D7B3] transition-colors">
                  Bow Ties
                </button>
              </li>
              <li>
                <button onClick={() => onNavigate('neckties')} className="hover:text-[#E6D7B3] transition-colors">
                  Neckties
                </button>
              </li>
              <li>
                <button onClick={() => onNavigate('new-arrivals')} className="hover:text-[#E6D7B3] transition-colors">
                  New Arrivals
                </button>
              </li>
              <li>
                <button onClick={() => onNavigate('best-sellers')} className="hover:text-[#E6D7B3] transition-colors">
                  Best Sellers
                </button>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>
                <button onClick={() => onNavigate('about')} className="hover:text-[#E6D7B3] transition-colors">
                  About Johnnie
                </button>
              </li>
              <li>
                <button onClick={() => onNavigate('resources')} className="hover:text-[#E6D7B3] transition-colors">
                  Resources
                </button>
              </li>
              <li>
                <button onClick={() => onNavigate('contact')} className="hover:text-[#E6D7B3] transition-colors">
                  Contact
                </button>
              </li>
              <li>
                <a href="#" className="hover:text-[#E6D7B3] transition-colors">
                  Shipping & Returns
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-[#E6D7B3] transition-colors">
                  Privacy Policy
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Newsletter</h4>
            <p className="text-gray-300 text-sm mb-4">
              Subscribe to receive updates on new arrivals and special offers.
            </p>
            <div className="flex">
              <input
                type="email"
                placeholder="Your email"
                className="flex-1 px-4 py-2 rounded-l-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#7B1E2B]"
              />
              <button className="px-4 py-2 bg-[#7B1E2B] rounded-r-md hover:bg-[#E6D7B3] hover:text-[#001B2A] transition-colors">
                <Mail className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-center sm:text-left text-sm text-gray-400">
              &copy; 2025 The Bow Tie Guy - Johnnie Mayo. All rights reserved. Crafting style since 1977.
            </p>
            
            {/* Design Customizer Link for Client */}
            <button
              onClick={() => navigate('/customize')}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-md transition-colors text-sm"
            >
              <Settings className="h-4 w-4" />
              Design Options
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
